import type { SelectedProductTile } from '@epilot/journey-logic-commons'
import { isBlockFact } from '@epilot/journey-logic-commons/src/next/references'
import {
  productsBlockValueSchema,
  productSelectionComparisonValueSchema,
  productSelectionFunctionClauseSchema
} from '@epilot/journey-logic-commons/src/schemas/v4'
import type { ProductSelectionTuples } from '@epilot/journey-logic-commons/src/types/next'
import {
  LOGIC_OPERATOR,
  PRODUCTS_FUNCTIONS
} from '@epilot/journey-logic-commons/src/types/next'

import type { Operator } from '../operator'
import { Contain } from '../operators/contain'
import { ContainEither } from '../operators/containEither'
import { CustomFunction } from '../operators/customFunction'
import { Equal } from '../operators/equal'
import { NotContain } from '../operators/notContain'

const ArgsSchema = productSelectionFunctionClauseSchema.shape.args

const ALLOWED_OPERATOR_MAP: Partial<Record<LOGIC_OPERATOR, Operator>> = {
  [LOGIC_OPERATOR.EQUAL]: new Equal(),
  [LOGIC_OPERATOR.IN]: new ContainEither(),
  [LOGIC_OPERATOR.NOT_IN]: new NotContain(),
  [LOGIC_OPERATOR.CONTAIN_EITHER]: new ContainEither(),
  [LOGIC_OPERATOR.CONTAIN]: new Contain(),
  [LOGIC_OPERATOR.NOT_CONTAIN]: new NotContain()
}

export const productSelectionFunction = new CustomFunction(
  PRODUCTS_FUNCTIONS.PRODUCT_SELECTION,
  async (uncastFactValue, uncastComparisonValue, context, args, _signal) => {
    if (!isBlockFact(context?.fact)) return true

    const { operator } = ArgsSchema.parse(args)
    const factValue = productsBlockValueSchema.parse(uncastFactValue)
    const factArray = Array.isArray(factValue) ? factValue : [factValue]

    const newFactValue = factArray.reduce<ProductSelectionTuples>(
      (acc, item: SelectedProductTile) => {
        if (
          typeof item?.product.selectedProductId !== 'undefined' &&
          typeof item?.product.selectedPriceId !== 'undefined'
        ) {
          acc.push([
            item.product.selectedProductId,
            item.product.selectedPriceId
          ])
        }

        return acc
      },
      []
    )

    const Operator = ALLOWED_OPERATOR_MAP[operator]

    if (!Operator) {
      throw new Error(`Unsupported operator: ${operator}`)
    }

    const newComparisonValue: ProductSelectionTuples =
      productSelectionComparisonValueSchema
        .parse(uncastComparisonValue)
        .map((item) => [item.productId, item.priceId])

    return Operator.evaluate(newFactValue, newComparisonValue as never)
  },
  200 // should be much faster
)
